// Fire and smoke drawn the way games draw them: not solid shapes but camera-facing cards
// shaded on the GPU. Each fire is a few upright flame tongues of scrolling noise (white-hot
// at the root, orange, then dark red licking off the tips), a spray of embers riding the
// heat, a flickering glow on the ground round it, and a column of soft, ragged smoke that
// rises, spreads and drifts downwind. Everything moves in the shaders from one time value,
// so a frame costs two draw calls and no work on the CPU, however many fires burn.
import * as THREE from 'three';

const NOISE = /* glsl */ `
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int k = 0; k < 4; k++) { v += a * noise(p); p = p * 2.03 + 17.1; a *= 0.5; }
    return v;
  }
`;

/** What each card is: x the seed, y the fire's size, z where in its life cycle it starts, w its kind. */
const KIND = { tongue: 0, ember: 1, glow: 2 } as const;

const FLAME_VERT = /* glsl */ `
  uniform float uTime;
  attribute vec4 aData;
  varying vec2 vUv;
  varying float vSeed;
  varying float vLife;
  varying float vKind;
  void main() {
    float seed = aData.x, size = aData.y, phase = aData.z, kind = aData.w;
    vec3 at = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    vec3 right = normalize(vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]));
    vec3 up = normalize(vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]));
    vUv = uv; vSeed = seed; vKind = kind; vLife = 0.0;
    vec3 world;
    if (kind < 0.5) {
      // a tongue of flame: stands upright on the fire, turned to face the camera, breathing in height
      vec3 c = (modelMatrix * vec4(at, 1.0)).xyz;
      vec3 r = normalize(vec3(right.x, 0.0, right.z));
      float breathe = 0.85 + 0.15 * sin(uTime * (6.0 + seed * 3.0) + seed * 20.0) + 0.1 * sin(uTime * 13.7 + seed * 7.0);
      world = c + r * (uv.x - 0.5) * size * 1.15 + vec3(0.0, 1.0, 0.0) * (uv.y - 0.05) * size * 1.7 * breathe;
    } else if (kind < 1.5) {
      // an ember: thrown up on the heat, wandering, cooling as it climbs
      float life = fract(uTime / 1.8 + phase);
      vLife = life;
      vec3 drift = vec3(sin(life * 6.0 + seed * 13.0) * 0.3 * size + life * life * 0.9 * size,
                        life * 2.8 * size + 0.2 * size,
                        cos(life * 5.0 + seed * 7.0) * 0.25 * size + life * 0.3 * size);
      vec3 c = (modelMatrix * vec4(at + drift, 1.0)).xyz;
      world = c + (right * (uv.x - 0.5) + up * (uv.y - 0.5)) * 0.07 * max(size, 0.6);
    } else {
      // the glow the fire throws on the ground round it
      world = (modelMatrix * vec4(at + vec3((uv.x - 0.5) * size * 3.0, 0.03, (uv.y - 0.5) * size * 3.0), 1.0)).xyz;
    }
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  }
`;

const FLAME_FRAG = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  varying float vSeed;
  varying float vLife;
  varying float vKind;
  ${NOISE}
  vec3 ramp(float heat) {
    // dark red to orange to yellow to white, like a real flame's temperature
    vec3 c = mix(vec3(0.35, 0.02, 0.0), vec3(0.95, 0.22, 0.01), smoothstep(0.05, 0.4, heat));
    c = mix(c, vec3(1.0, 0.55, 0.08), smoothstep(0.4, 0.8, heat));
    return mix(c, vec3(1.0, 0.85, 0.45), smoothstep(0.9, 1.2, heat));
  }
  void main() {
    if (vKind < 0.5) {
      float h = vUv.y;
      // rising, curling noise: the flame's body tears upward
      float n = fbm(vec2(vUv.x * 3.2 + vSeed * 9.0, h * 2.4 - uTime * 2.6 + vSeed * 4.0));
      float n2 = fbm(vec2(vUv.x * 6.0 - vSeed * 3.0, h * 4.5 - uTime * 4.1));
      float sway = (n - 0.5) * 0.35 * h + sin(uTime * 3.0 + vSeed * 10.0 + h * 4.0) * 0.06 * h;
      float dx = abs(vUv.x - 0.5 + sway);
      // a teardrop: wide and round at the root, drawn out to ragged tips
      float width = mix(0.4, 0.03, pow(h, 0.75)) * (0.7 + 0.6 * n2);
      float body = smoothstep(width, width * 0.25, dx);
      float top = smoothstep(0.95, 0.35, h + (n - 0.5) * 0.55);
      float root = smoothstep(0.0, 0.12, h);
      float heat = body * top * root * (0.55 + 0.9 * n);
      if (heat < 0.02) discard;
      vec3 col = ramp(heat) * (0.95 + 1.1 * heat);
      gl_FragColor = vec4(col * min(heat, 1.0), 1.0);
    } else if (vKind < 1.5) {
      float d = length(vUv - 0.5) * 2.0;
      float a = smoothstep(1.0, 0.0, d) * (1.0 - vLife) * smoothstep(0.0, 0.08, vLife);
      // twinkles out now and then
      a *= step(0.25, fract(sin(vSeed * 91.7 + floor(uTime * 9.0)) * 43758.5));
      gl_FragColor = vec4(vec3(1.0, 0.55, 0.15) * 4.0 * a, 1.0);
    } else {
      float d = length(vUv - 0.5) * 2.0;
      float flick = 0.75 + 0.25 * sin(uTime * 11.0 + vSeed * 30.0) * sin(uTime * 7.3 + vSeed);
      float a = pow(max(0.0, 1.0 - d), 2.2) * flick;
      gl_FragColor = vec4(vec3(1.0, 0.36, 0.06) * 0.8 * a, 1.0);
    }
  }
`;

const SMOKE_VERT = /* glsl */ `
  uniform float uTime;
  attribute vec4 aData;
  varying vec2 vUv;
  varying float vSeed;
  varying float vLife;
  void main() {
    float seed = aData.x, size = aData.y, phase = aData.z;
    float life = fract(uTime / 6.0 + phase);
    vLife = life;
    vUv = uv; vSeed = seed;
    vec3 at = (instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    // rises and slows, spreading and leaning downwind (east and a little south)
    float s = max(size, 0.6);
    vec3 drift = vec3(life * life * 3.0 * s + sin(life * 5.0 + seed * 9.0) * 0.2 * s,
                      0.5 * s + (1.0 - (1.0 - life) * (1.0 - life)) * 6.5 * s,
                      life * 1.1 * s + cos(life * 4.0 + seed * 5.0) * 0.2 * s);
    vec3 c = (modelMatrix * vec4(at + drift, 1.0)).xyz;
    vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
    vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
    // each puff turns slowly as it climbs
    float a = seed * 6.28 + life * (seed - 0.5) * 2.0;
    vec2 p = mat2(cos(a), -sin(a), sin(a), cos(a)) * (uv - 0.5);
    float r = s * (0.5 + life * 2.6);
    gl_Position = projectionMatrix * viewMatrix * vec4(c + (right * p.x + up * p.y) * r, 1.0);
  }
`;

const SMOKE_FRAG = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  varying float vSeed;
  varying float vLife;
  ${NOISE}
  void main() {
    vec2 q = vUv - 0.5;
    float n = fbm(vUv * 3.0 + vec2(vSeed * 17.0, vSeed * 5.0 - uTime * 0.15));
    float d = length(q) * 2.0 + (n - 0.5) * 0.9;
    float body = smoothstep(1.0, 0.25, d);
    float fade = smoothstep(0.0, 0.12, vLife) * pow(1.0 - vLife, 1.3);
    float alpha = body * fade * 0.72;
    if (alpha < 0.01) discard;
    // lit from above, lit orange underneath by the fire while it is still low
    vec3 col = mix(vec3(0.07, 0.065, 0.06), vec3(0.22, 0.21, 0.2), clamp(0.5 - q.y + n * 0.5, 0.0, 1.0) * 0.8 + vLife * 0.3);
    col += vec3(0.5, 0.18, 0.02) * (1.0 - smoothstep(0.0, 0.25, vLife)) * smoothstep(0.2, -0.4, q.y);
    gl_FragColor = vec4(col, alpha);
  }
`;

const TONGUES = 5, EMBERS = 10, PUFFS = 12;

export class Fires {
  readonly group = new THREE.Group();
  private readonly time = { value: 0 };
  private readonly flames: number[][] = [];
  private readonly smoke: number[][] = [];
  private readonly at: THREE.Vector3[] = [];
  private readonly smokeAt: THREE.Vector3[] = [];

  constructor() {
    this.group.name = 'fires';
  }

  /** A fire at a point in the group's frame, `size` about 1 for a burning car. */
  add(x: number, y: number, z: number, size: number, seed: number): void {
    const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); };
    let k = 0;
    this.at.push(new THREE.Vector3(x, y, z));
    this.flames.push([r(k++), size, 0, KIND.glow]);
    for (let i = 0; i < TONGUES; i++) {
      // the biggest tongue in the middle, smaller ones round it
      const s = size * (i === 0 ? 1 : 0.6 + r(k++) * 0.35);
      const a = (i / TONGUES) * Math.PI * 2 + r(k++), d = i === 0 ? 0 : size * (0.18 + r(k++) * 0.2);
      this.at.push(new THREE.Vector3(x + Math.cos(a) * d, y, z + Math.sin(a) * d));
      this.flames.push([r(k++), s, 0, KIND.tongue]);
    }
    for (let i = 0; i < EMBERS; i++) {
      this.at.push(new THREE.Vector3(x + (r(k++) - 0.5) * size * 0.4, y, z + (r(k++) - 0.5) * size * 0.4));
      this.flames.push([r(k++), size, i / EMBERS + r(k++) * 0.1, KIND.ember]);
    }
    for (let i = 0; i < PUFFS; i++) {
      this.smokeAt.push(new THREE.Vector3(x + (r(k++) - 0.5) * size * 0.3, y, z + (r(k++) - 0.5) * size * 0.3));
      this.smoke.push([r(k++), size, i / PUFFS, 0]);
    }
  }

  /** Makes the meshes, once every fire is added. */
  build(): void {
    const card = new THREE.PlaneGeometry(1, 1);
    const flameMat = new THREE.ShaderMaterial({
      uniforms: { uTime: this.time }, vertexShader: FLAME_VERT, fragmentShader: FLAME_FRAG,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide,
    });
    const smokeMat = new THREE.ShaderMaterial({
      uniforms: { uTime: this.time }, vertexShader: SMOKE_VERT, fragmentShader: SMOKE_FRAG,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
    });
    // smoke first, so the flames add their light over it
    this.group.add(this.instanced(card, smokeMat, this.smokeAt, this.smoke, 1));
    this.group.add(this.instanced(card, flameMat, this.at, this.flames, 2));
  }

  private instanced(geo: THREE.BufferGeometry, mat: THREE.Material, at: THREE.Vector3[], data: number[][], order: number): THREE.InstancedMesh {
    const g = geo.clone();
    g.setAttribute('aData', new THREE.InstancedBufferAttribute(new Float32Array(data.flat()), 4));
    const m = new THREE.InstancedMesh(g, mat, Math.max(1, at.length));
    m.count = at.length;
    const mx = new THREE.Matrix4();
    at.forEach((p, i) => m.setMatrixAt(i, mx.makeTranslation(p.x, p.y, p.z)));
    // the cards are placed in the shaders, far from where the geometry says
    m.frustumCulled = false;
    m.renderOrder = order;
    return m;
  }

  /** Moves every fire on. `now` is milliseconds. */
  update(now: number): void {
    this.time.value = (now / 1000) % 3600;
  }
}
